import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, Calendar } from 'lucide-react';
import { useSubmitManualLead } from '@/hooks/useLeads';
import { useAuthStore } from '@/stores/useAuthStore';
import { Button, Input, Card, CountryCombobox } from '@/components/ui';
import { useT } from '@/i18n/LanguageContext';

export default function ManualEntryPage() {
  const navigate = useNavigate();
  const submitLead = useSubmitManualLead();
  const selectedEventId = useAuthStore((s) => s.selectedEventId);
  const t = useT();

  const [form, setForm] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    website: '',
    title: '',
    country: '',
  });
  const [error, setError] = useState('');

  // No event selected guard
  if (!selectedEventId) {
    return (
      <div className="p-4 md:p-6 flex items-center justify-center min-h-[400px]">
        <Card className="p-8 text-center max-w-sm">
          <Calendar size={32} className="mx-auto text-reevol-text-muted mb-3" />
          <h2 className="text-lg font-bold text-reevol-text mb-2">{t.common.selectEvent}</h2>
          <p className="text-sm text-reevol-text-muted mb-4">
            {t.common.chooseEventManual}
          </p>
          <Button onClick={() => navigate('/events')}>
            {t.common.goToEvents}
          </Button>
        </Card>
      </div>
    );
  }

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.fullName.trim() || !form.companyName.trim()) {
      setError(t.manual.fieldsRequired);
      return;
    }

    try {
      const result = await submitLead.mutateAsync({
        fullName: form.fullName.trim(),
        companyName: form.companyName.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        website: form.website.trim() || undefined,
        title: form.title.trim() || undefined,
        country: form.country.trim() || undefined,
        eventId: selectedEventId,
      });
      navigate(`/enriching/${result.enrichmentJobId}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t.manual.somethingWrong);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-lg mx-auto">
      {/* Back */}
      <button
        onClick={() => navigate('/scan')}
        className="flex items-center gap-1 text-sm text-reevol-text-muted hover:text-reevol-blue mb-4 transition-colors"
      >
        <ArrowLeft size={16} />
        {t.common.back}
      </button>

      <h1 className="text-xl font-bold text-reevol-text mb-1">{t.manual.title}</h1>
      <p className="text-sm text-reevol-text-muted mb-1">{t.manual.subtitle}</p>

      <button
        onClick={() => navigate('/scan')}
        className="flex items-center gap-1.5 text-sm font-medium text-reevol-blue hover:text-reevol-blue/80 transition-colors mb-5"
      >
        <Camera size={14} />
        {t.manual.scanInstead}
      </button>

      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label={t.manual.fullName}
            placeholder={t.manual.fullNamePlaceholder}
            value={form.fullName}
            onChange={set('fullName')}
            required
            autoFocus
          />
          <Input
            label={t.manual.companyName}
            placeholder={t.manual.companyPlaceholder}
            value={form.companyName}
            onChange={set('companyName')}
            required
          />
          <Input
            label={t.common.email}
            placeholder={t.manual.emailPlaceholder}
            type="email"
            value={form.email}
            onChange={set('email')}
          />
          <Input
            label={t.common.phone}
            placeholder={t.manual.phonePlaceholder}
            type="tel"
            value={form.phone}
            onChange={set('phone')}
          />
          <Input
            label={t.manual.websiteLabel}
            placeholder={t.manual.websitePlaceholder}
            value={form.website}
            onChange={set('website')}
          />
          <Input
            label={t.manual.jobTitle}
            placeholder={t.manual.jobTitlePlaceholder}
            value={form.title}
            onChange={set('title')}
          />
          <CountryCombobox
            label={t.manual.countryLabel}
            placeholder={t.manual.countryPlaceholder}
            value={form.country}
            onChange={(v) => setForm((f) => ({ ...f, country: v }))}
          />

          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>
          )}

          <Button type="submit" fullWidth loading={submitLead.isPending}>
            {t.manual.submit}
          </Button>
        </form>
      </Card>
    </div>
  );
}
